import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Home, Monitor, User } from 'lucide-react';
import { getMediaUrl } from '../services/api';
import { getSpecialistUrl } from '../utils/slugify';
import './TutorCard.css';

const TutorCard = ({ tutor, preview = false }) => {
  const getModeLabel = (mode) => {
    switch (mode) {
      case 'Online':
        return 'Օնլայն';
      case 'In-Person':
        return 'Իր մոտ';
      case 'Both':
      case 'Student-Location':
        return 'Ձեր մոտ';
      default:
        return mode;
    }
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'Online':
        return <Monitor size={16} />;
      case 'In-Person':
        return <Home size={16} />;
      case 'Both':
      case 'Student-Location':
        return <User size={16} />;
      default:
        return <User size={16} />;
    }
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    return getMediaUrl(photo);
  };

  // Format price in AMD (no conversion needed - backend stores AMD)
  const formatAmdPrice = (amdPrice) => {
    return Math.round(amdPrice).toLocaleString('hy-AM').replace(/,/g, ' ');
  };

  // Generate neutral placeholder avatar (initials)
  const getPlaceholderInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const cardInner = (
    <>
      <div className="card-layout">
        {/* Left: Square Avatar */}
        <div className="tutor-avatar">
          {getPhotoUrl(tutor.photo) ? (
            <img
              src={getPhotoUrl(tutor.photo)}
              alt={tutor.name}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div className="avatar-placeholder" style={{ display: getPhotoUrl(tutor.photo) ? 'none' : 'flex' }}>
            <span className="avatar-initials">{getPlaceholderInitials(tutor.name)}</span>
          </div>
        </div>

        {/* Right: Content */}
        <div className="card-content">
          <div className="content-header">
            <div className="header-left">
              <h3 className="tutor-name">{tutor.name}</h3>
              
              {/* Subjects */}
              <div className="subjects-row">
                {tutor.subjects.slice(0, 3).map((subject, index) => (
                  <span key={index} className="subject-tag">
                    {subject}
                  </span>
                ))}
                {tutor.subjects.length > 3 && (
                  <span className="subject-tag more">+{tutor.subjects.length - 3}</span>
                )}
              </div>
            </div>

            {/* Price Badge - Top Right */}
            <div className="price-badge-compact">
              {formatAmdPrice(tutor.hourlyRate)} դր/ժ
            </div>
          </div>

          {/* Info Grid */}
          <div className="info-grid">
            <div className="info-item">
              <MapPin size={14} />
              <span>{tutor.location}</span>
            </div>

            <div className="info-item">
              <Clock size={14} />
              <span>{tutor.experience}</span>
            </div>

            {tutor.modes && tutor.modes.length > 0 && (
              <div className="info-item modes">
                {tutor.modes.slice(0, 2).map((mode, index) => (
                  <span key={index} className="mode-label">
                    {getModeIcon(mode)}
                    {getModeLabel(mode)}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Description - 2 lines max */}
          <p className="card-description">
            {tutor.description}
          </p>

          {/* CTA — real button in list; static label in dashboard preview */}
          {preview ? (
            <span className="view-profile-btn view-profile-btn-static">Դիտել պրոֆիլ</span>
          ) : (
            <span className="view-profile-btn">Դիտել պրոֆիլ</span>
          )}
        </div>
      </div>
    </>
  );

  if (preview) {
    return (
      <div className="tutor-card tutor-card--preview" role="region" aria-label="Պրոֆիլի նախադիտում">
        {cardInner}
      </div>
    );
  }

  return (
    <Link to={getSpecialistUrl(tutor)} className="tutor-card">
      {cardInner}
    </Link>
  );
};

export default TutorCard;
